/**
 * Chart registry — the single list of "what charts exist for a room's
 * simulation results" and what data each one needs. Platform-agnostic: no
 * React, no renderer, no backend response types. Web/mobile each keep their
 * own `id -> component` map (their charts look different — Canvas2D vs Skia
 * — but agree on this one list of what exists and when it applies).
 *
 * To add a chart: add one `ChartDefinition` to `CHART_REGISTRY` here, then
 * one entry to the platform's id->component map. To remove/hide one: delete
 * the registry entry (or, once a "choose your charts" UI exists, drop its id
 * from the user's enabled-set — `availableCharts` already takes one).
 *
 * `isAvailable` answers "does this chart make sense in this room's context at
 * all" (e.g. is there a freq-response slot to plug into), NOT "has the user
 * clicked Compute yet" — an available-but-uncomputed chart still renders
 * itself, showing its own "no data yet" state, same as the existing charts do.
 */

/** One speaker's own complex contribution to the combined mag/phase — see
 *  Per-Speaker FR/Phase charts. */
export interface ChartPerSpeaker {
  index: number;
  subkind: string | null;
  mag?: number[];
  phase?: number[];
}

export interface ChartFreqData {
  freqs: number[];
  mag: number[];
  magBaseline?: number[] | null;
  /** Radians, unwrapped. Absent until the backend adds it (see phase chart). */
  phase?: number[] | null;
  phaseBaseline?: number[] | null;
  perSpeaker?: ChartPerSpeaker[] | null;
}

/** Per-band energy decay curve — the raw data a waterfall/spectrogram/decay-fit
 *  chart draws from. `edcDb[i]` is band `bandHz[i]`'s decay over `timeS`,
 *  0 dB at t=0 and decreasing. */
export interface RT60DecayCurve {
  bandHz: number[];
  timeS: number[];
  edcDb: number[][];
}

/** A band with zero arrivals is filled with a flat curve at this level
 *  (see backend/ray_rt60.py's NO_ENERGY_SENTINEL_DB — keep both in sync),
 *  distinguishable from a real decay because a real curve is always exactly
 *  0 dB at t=0. Compare with `edcDb[i][0] < NO_ENERGY_SENTINEL_DB + 1` rather
 *  than exact equality, matching the backend's own detection tolerance. */
export const NO_ENERGY_SENTINEL_DB = -300;

export interface RT60Fits {
  bandHz: number[];
  edt: number[];
  t20: number[];
  t30: number[];
  topt: number[];
}

export interface ChartRt60Data {
  bandHz: number[];
  rt60: number[];
  rt60Baseline?: number[] | null;
  fineHz: number[];
  fineRt60: number[];
  fineRt60Baseline?: number[] | null;
  decay?: RT60DecayCurve | null;
  decayBaseline?: RT60DecayCurve | null;
  fits?: RT60Fits | null;
  /** Per-mode resonant frequency + Q, <200 Hz — feeds the Modal Ringing
   *  Fingerprint chart (see `computeModalRingingFingerprint`). */
  modes?: { freq_hz: number; q: number }[] | null;
}

export interface ChartRoomData {
  freq?: ChartFreqData | null;
  rt60?: ChartRt60Data | null;
  roomDims?: { Lx: number; Ly: number; H: number } | null;
}

export type ChartCategory = 'frequency' | 'decay' | 'modal';

export interface ChartDefinition {
  id: string;
  title: string;
  category: ChartCategory;
  /** One line: what it shows and why it's useful — reused verbatim by any
   *  future "choose your charts" settings UI, so write it for that reader. */
  description: string;
  isAvailable: (data: ChartRoomData) => boolean;
}

export const CHART_REGISTRY = [
  {
    id: 'freq_response',
    title: 'Frequency Response',
    category: 'frequency',
    description: 'SPL vs. frequency (20-200 Hz), before/after overlay — plain curve, no octave smoothing or target-curve overlay. See freq_response_per_octave for those.',
    isAvailable: (d) => d.freq != null,
  },
  {
    id: 'freq_response_per_octave',
    title: 'Frequency Response (Per-Octave)',
    category: 'frequency',
    description: 'SPL vs. frequency (20-200 Hz), before/after overlay, with selectable fractional-octave smoothing (1/24-1/3 oct) and an optional target-curve overlay (flat / gentle tilt).',
    isAvailable: (d) => d.freq != null,
  },
  {
    id: 'rta',
    title: 'RTA',
    category: 'frequency',
    description: 'Frequency response re-binned into selectable fractional-octave bars (1/1-1/12 oct).',
    isAvailable: (d) => d.freq != null,
  },
  {
    id: 'phase',
    title: 'Phase',
    category: 'frequency',
    description: 'Unwrapped phase (degrees) of the modal transfer function vs. frequency.',
    isAvailable: (d) => !!d.freq?.phase?.length,
  },
  {
    id: 'group-delay',
    title: 'Group Delay',
    category: 'frequency',
    description: 'Group delay (ms) — the derivative of phase; large values mark sharp modal resonances.',
    isAvailable: (d) => !!d.freq?.phase?.length,
  },
  {
    id: 'impulse-response',
    title: 'Impulse Response',
    category: 'frequency',
    description: 'Time-domain impulse response, reconstructed from the measured complex frequency response (band-limited 20-20 kHz, no new backend data).',
    isAvailable: (d) => !!d.freq?.phase?.length,
  },
  {
    id: 'step-response',
    title: 'Step Response',
    category: 'frequency',
    description: 'Running sum of the impulse response — shows the room\'s time-domain settling behaviour rather than its instantaneous decay.',
    isAvailable: (d) => !!d.freq?.phase?.length,
  },
  {
    id: 'per-speaker-freq-response',
    title: 'Per-Speaker FR',
    category: 'frequency',
    description: 'One frequency-response line per speaker/sub, isolated from the combined response — spot a weak or misbehaving channel.',
    isAvailable: (d) => !!d.freq?.perSpeaker?.length,
  },
  {
    id: 'per-speaker-phase',
    title: 'Per-Speaker Phase',
    category: 'frequency',
    description: 'One phase line per speaker/sub — spot a channel whose phase diverges at the crossover region.',
    isAvailable: (d) => !!d.freq?.perSpeaker?.some((s) => s.phase?.length),
  },
  {
    id: 'modal-density',
    title: 'Modal Density',
    category: 'modal',
    description: 'Room-mode count per 1/3-octave band (20-300 Hz), checked against the Bonello density criterion, with the Schroeder frequency marked.',
    isAvailable: (d) => d.roomDims != null,
  },
  {
    id: 'spl-uniformity',
    title: 'SPL Uniformity',
    category: 'modal',
    description: 'A room-plan heatmap of relative SPL at the listener\'s ear height, one frequency at a time — how evenly the modal field covers the seating area (EASE coverage-map pattern).',
    isAvailable: (d) => d.rt60 != null,
  },
  {
    id: 'sweet-spot-map',
    title: 'Sweet Spot Map',
    category: 'modal',
    description: 'A room-plan heatmap of modal-band SPL uniformity — sweep listening position (Mode A) to see how consistently a chosen frequency band covers every seat, or sweep speaker position (Mode B) to find where the source itself belongs for the most consistent bass at a fixed seat.',
    isAvailable: (d) => d.rt60 != null,
  },
  {
    id: 'modal-ringing-fingerprint',
    title: 'Modal Ringing Fingerprint',
    category: 'modal',
    description: 'Room modes ranked by how long they audibly ring (2.2Q/(pi*f)) — a short, prioritized "fix this frequency first" list rather than a curve to interpret.',
    isAvailable: (d) => !!d.rt60?.modes?.length,
  },
  {
    id: 'echogram',
    title: 'Echogram',
    category: 'decay',
    description: 'Discrete ray arrivals as time vs. level (EASE-style echogram) — reflections only, no direct sound.',
    isAvailable: (d) => d.rt60 != null,
  },
  {
    id: 'rt60-sub',
    title: 'RT60 · 20-200 Hz',
    category: 'decay',
    description: 'Modal-band decay time vs. frequency, studio target band shaded.',
    isAvailable: (d) => d.rt60 != null,
  },
  {
    id: 'rt60-above',
    title: 'RT60 · 200 Hz-20 kHz',
    category: 'decay',
    description: 'Ray-traced broadband decay time vs. frequency.',
    isAvailable: (d) => d.rt60 != null,
  },
  {
    id: 'waterfall',
    title: 'Waterfall',
    category: 'decay',
    description: 'Cumulative spectral decay — cascaded decay-vs-frequency traces over time, >=200 Hz.',
    isAvailable: (d) => d.rt60?.decay != null,
  },
  {
    id: 'spectrogram',
    title: 'Spectrogram',
    category: 'decay',
    description: 'The same decay curve as Waterfall, shown as a time x frequency heatmap, >=200 Hz.',
    isAvailable: (d) => d.rt60?.decay != null,
  },
  {
    id: 'decay-fit-comparison',
    title: 'Decay Fit Comparison',
    category: 'decay',
    description: 'EDT/T20/T30/Topt decay-fit methods overlaid vs. frequency — a big spread flags a non-exponential decay.',
    isAvailable: (d) => d.rt60?.decay != null,
  },
  {
    id: 'clarity',
    title: 'Clarity',
    category: 'decay',
    description: 'C50/C80 (ISO 3382 clarity) vs. frequency — early-to-late energy ratio; higher is clearer, less "muddy".',
    isAvailable: (d) => d.rt60?.decay != null,
  },
  {
    id: 'approx-sti',
    title: 'Approx. STI',
    category: 'decay',
    description: 'A simplified speech-intelligibility estimate (Approx. STI, derived from octave-band C50) — not a certified IEC 60268-16 measurement, but enough to flag a room clearly good or bad for speech.',
    isAvailable: (d) => d.rt60?.decay != null,
  },
  {
    id: 'stereo-image-skew',
    title: 'Stereo Image Skew',
    category: 'frequency',
    description: 'Inter-ear level/time differences vs. frequency at the listening seat — reveals room-induced stereo image asymmetry left-right.',
    isAvailable: (d) => d.freq != null,
  },
] as const satisfies readonly ChartDefinition[];

/** Every id `CHART_REGISTRY` actually defines, derived from the registry
 *  itself rather than hand-maintained — adding/renaming/removing a chart
 *  here automatically updates this union. A platform's `id -> component`
 *  dispatch map should be typed `Record<ChartId, Component>` (exact, not
 *  `Partial`) so a missing or stale entry is a compile error instead of
 *  waiting on the runtime completeness test (registry-parity.test.ts /
 *  chart-registry-parity.test.ts) to catch it. */
export type ChartId = (typeof CHART_REGISTRY)[number]['id'];

/** Charts shown on the guided **Results** step (web StepResult / mobile
 *  step-result). Edit this list to change which registry charts appear there
 *  — platform `id -> component` maps stay unchanged; pass as `enabledIds` to
 *  `availableCharts()`. Non-registry blocks on that step (audio preview,
 *  soundstage, recommended products) live in each platform's step component. */
export const RESULTS_CHART_IDS: ReadonlySet<ChartId> = new Set([
  'freq_response',
  'rt60-sub',
  'rt60-above',
  'impulse-response',
]);

/** Charts shown by the free **Room Acoustics Simulator**
 *  (`/tools/room-acoustics-simulator`, plan S23): the bass response, the
 *  ranked mode list and decay time in both bands. Existing charts only; the
 *  simulator never gets a chart kind of its own. */
export const SIMULATOR_CHART_IDS: ReadonlySet<ChartId> = new Set([
  'freq_response',
  'modal-ringing-fingerprint',
  'rt60-sub',
  'rt60-above',
]);

/** Charts shown on the guided **Choose-your-placement** step's Room
 *  acoustics card (web StepGenerate / mobile step-generate). Per-candidate
 *  payloads come from the solver's post-rank `chart_freq` / `chart_rt60`.
 *  Edit this list to change which registry charts appear here — platform
 *  `id -> component` maps stay unchanged; pass as `enabledIds` to
 *  `availableCharts()`. */
export const PLACEMENT_CHART_IDS: ReadonlySet<ChartId> = new Set([
  'rt60-sub',
  'rt60-above',
  'impulse-response',
]);

/** Registry entries this room's data supports, optionally further filtered
 *  to a user's chosen id set (pass null/undefined to mean "all available"). */
export function availableCharts(
  data: ChartRoomData,
  enabledIds?: ReadonlySet<string> | null,
): ChartDefinition[] {
  return CHART_REGISTRY.filter(
    (c) => (enabledIds ? enabledIds.has(c.id) : true) && c.isAvailable(data),
  );
}
